import {
    CSSProperties,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Position, Range } from 'types'
import DraggableContext from 'ui/src/components/draggable/context'
import { gifFromMediaSprite } from '../../utils/media'
import { GifGenerationError } from '../../utils/media/gif/generation-error'
import { ArtboardContext } from './context'
import { useLayers } from './hooks/use-layers'
import { useRenderer } from './hooks/use-renderer'
import { useShader } from './hooks/use-shader'
import Loop from './loop'
import { Container, RasterizedCanvas, RenderError } from './style'
import { ArtboardFilter, ArtboardLoopRef, ArtboardRasterizedRef, ArtboardRef } from './types'
import { createSprite } from './utilities/create-sprite'

export { default as GIF } from './layers/gif'
export { default as Image } from './layers/image'
export { default as Sprite } from './layers/sprite'
export { default as Text } from './layers/text'
export { default as Texture } from './layers/texture'

type Props = {
    children?: ReactNode
    className?: string
    desynchronized?: boolean
    duration: number
    filter?: ArtboardFilter
    filterIntensity?: number
    disableLoop?: boolean
    fps?: number
    height: number
    paused?: boolean
    preserveDrawingBuffer?: boolean
    style?: CSSProperties
    transparency?: boolean
    trim?: Range
    width: number
    onRenderError?: (error: Error) => void
}

const Artboard = (
    {
        children,
        className,
        desynchronized = true,
        duration,
        filter,
        filterIntensity = 1,
        disableLoop = false,
        fps = 15,
        height,
        paused = false,
        preserveDrawingBuffer = false,
        style = {},
        transparency = false,
        trim,
        width,
        onRenderError,
    }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const { nodes, refs: layers } = useLayers(children)
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
    const loopRef = useRef<ArtboardLoopRef>(null)
    const rasterizedRef = useRef<ArtboardRasterizedRef>(null)
    const [renderError, setRenderError] = useState<Error | null>(null)
    const onRendererError = useCallback(
        (error: Error) => {
            setRenderError(error)
            onRenderError?.(error)
        },
        [onRenderError]
    )
    const renderer = useRenderer(canvasRef.current, layers, transparency, onRendererError, {
        desynchronized,
        preserveDrawingBuffer,
    })
    const shader = useShader(renderer)
    const frames = useMemo(() => Math.floor(duration * fps), [duration])
    const delay = useMemo(() => 1 / fps, [fps])

    useEffect(() => {
        if (renderer) {
            setRenderError(null)
        }
    }, [renderer])

    function onLoop(progress: number) {
        rasterizedRef.current?.renderer?.render(progress)
    }

    useImperativeHandle(ref, () => ({
        createGIF: async (crop?: Position) => {
            loopRef.current?.pause()

            try {
                const exportRenderer = rasterizedRef.current?.renderer

                if (!exportRenderer) {
                    throw new GifGenerationError('renderer_unavailable', 'The GIF export renderer was unavailable')
                }

                let sprite

                try {
                    sprite = await createSprite(frames, fps, trim, exportRenderer, crop)
                } catch (error) {
                    throw new GifGenerationError('render_failed', 'Rendering GIF frames failed', error)
                }

                if (!sprite) {
                    throw new GifGenerationError('render_failed', 'Rendering GIF frames returned no sprite')
                }

                try {
                    return await gifFromMediaSprite(sprite)
                } catch (error) {
                    if (error instanceof GifGenerationError) {
                        throw error
                    }

                    throw new GifGenerationError('encoding_failed', 'Encoding the rendered GIF frames failed', error)
                }
            } finally {
                loopRef.current?.play()
            }
        },
        createSprite: async (crop?: Position) => {
            loopRef.current?.pause()

            const sprite = await createSprite(frames, fps, trim, rasterizedRef.current?.renderer, crop)

            loopRef.current?.play()

            return sprite
        },
        getProgress: () => {
            return loopRef.current?.progress || 0
        },
        setProgress: (progress: number) => {
            loopRef.current?.scrub(progress)
        },
    }))

    return (
        <ArtboardContext.Provider
            value={{ frames, filter, filterIntensity, fps, height, paused, renderer, shader, width }}
        >
            <Container className={className} style={{ height, width, ...style }}>
                {renderError ? (
                    <RenderError role="alert">
                        <strong>Unable to load the editor</strong>
                        <span>Turn on graphics acceleration in your browser settings, then reload this page.</span>
                    </RenderError>
                ) : (
                    <>
                        {!disableLoop && (
                            <Loop
                                delay={delay}
                                paused={paused}
                                frames={frames}
                                ref={loopRef}
                                trim={trim}
                                onLoop={onLoop}
                            />
                        )}
                        <RasterizedCanvas
                            desynchronized={desynchronized}
                            duration={duration}
                            filter={filter}
                            filterIntensity={filterIntensity}
                            preserveDrawingBuffer={preserveDrawingBuffer}
                            ref={rasterizedRef}
                            transparency={transparency}
                        />
                        <DraggableContext>{renderer && nodes}</DraggableContext>
                    </>
                )}
            </Container>
        </ArtboardContext.Provider>
    )
}

export default forwardRef(Artboard)
